/**
 * InterfacrManager.ts - UI Integration Manager
 * 
 * ARCHITECTURE: Integration Manager Pattern
 * Interfacr ensures that all UI operations have:
 * - Logging (via Loggr) - ALWAYS present
 * - Undo/Redo (via Backtrackr) - ALWAYS present
 * - Drag & Drop (via Draggr) - Integrated
 * - Navigation (via Navigatr) - Integrated
 * 
 * This prevents forgetting critical integrations when working with UI.
 * 
 * CHANGE HISTORY:
 * - 251125_2115 danielbeer: Refactor to Integration Manager pattern
 * - 251102_1627 danielbeer: Integrate Profylr for UI context and perspective
 * - 251026_0750 danielbeer: Initial creation - pervasive interface orchestrator
 * 
 * ARCHITECTURE NOTE: Interfacr as Integration Manager
 * 
 * Interfacr is designed to be nearly as pervasive as Actr, serving as the
 * central traffic controller for ALL interface functionality in OpCentral.
 * 
 * Key Responsibilities:
 * 1. Route all interface commands to appropriate packages
 * 2. Evaluate context before routing (via Contextr)
 * 3. Integrate with Actr for action orchestration
 * 4. Provide logging/debugging for all interface interactions
 * 5. Handle both user-triggered and external interface requests
 * 
 * Pattern: Universal Interface Orchestrator
 * - Single entry point for all interface operations
 * - Context-aware routing
 * - Middleware system for extensibility
 * - Event system for monitoring
 * 
 * Compare to Actr:
 * - Actr: Handles ALL actions (data + business logic)
 * - Interfacr: Handles ALL interface operations (UI + UX)
 * - Both work together: Actr → business logic, Interfacr → presentation
 * 
 * Integration with Handlr/Integratr:
 * - External interface requests come through Handlr/Integratr
 * - Interfacr evaluates context and routes to appropriate UI package
 * - Provides unified interface regardless of trigger source
 */

import type { Actr } from '@opcentral/actr';
import type { ProfileManager, ResolvedProfile } from '@opcentral/profylr';
import { DraggrManager } from '@opcentral/draggr';
import { Navigatr } from '@opcentral/navigatr';

// TODO: Type when Loggr exports proper types
type LoggrInstance = any;

/**
 * Interface command types
 */
export type InterfaceCommandType =
  | 'dialog'      // Modal dialogs (Promptr/Dialoggr)
  | 'panel'       // Side panels (Panelr)
  | 'form'        // Forms (Formr)
  | 'input'       // Inputs (Editr)
  | 'select'      // Selection UI (Selectr)
  | 'notification' // Notifications (Notifyr)
  | 'navigation'  // Navigation (Navigatr)
  | 'style'       // Styling (Stylr/Skinnr)
  | 'script'      // Scripting (Scriptr)
  | 'view'        // Views (Viewr)
  | 'custom';     // Custom interface operations

/**
 * Interface command
 */
export interface InterfaceCommand {
  type: InterfaceCommandType;
  operation: string;
  params?: Record<string, any>;
  context?: Record<string, any>;
  source?: 'user' | 'external' | 'system';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

/**
 * Interface command result
 */
export interface InterfaceResult {
  success: boolean;
  data?: any;
  error?: string;
  metadata?: {
    duration: number;
    package: string;
    operation: string;
  };
}

/**
 * Middleware function
 */
export type InterfaceMiddleware = (
  command: InterfaceCommand,
  next: () => Promise<InterfaceResult>
) => Promise<InterfaceResult>;

/**
 * Interface event
 */
export interface InterfaceEvent {
  type: 'command' | 'result' | 'error';
  command?: InterfaceCommand;
  result?: InterfaceResult;
  error?: Error;
  timestamp: number;
}

/**
 * Interface handler
 */
export type InterfaceHandler = (
  command: InterfaceCommand
) => Promise<InterfaceResult>;

/**
 * Interfacr configuration
 */
export interface InterfacrConfig {
  actr?: Actr;
  loggr?: LoggrInstance;
  profylr?: ProfileManager;
  backtrackr?: any;
  enableLogging?: boolean;
  enableEvents?: boolean;
  middleware?: InterfaceMiddleware[];
  handlers?: Map<string, InterfaceHandler>;
}

/**
 * InterfacrManager - Universal Interface Traffic Controller
 * 
 * Central orchestrator for all interface operations in OpCentral.
 * Routes commands to appropriate packages based on context.
 * 
 * PROFYLR INTEGRATION:
 * - Uses active profile for UI perspective (tone, style, language)
 * - Applies profile behavior settings to interface operations
 * - Context-aware UI rendering based on profile
 */
export class InterfacrManager {
  private actr?: Actr;
  private profylr?: ProfileManager;
  
  // ✅ INTEGRATION MANAGER: Required dependencies
  private loggr: LoggrInstance;
  private backtrackr: any; // TODO: Type when Backtrackr exports proper types
  private config: InterfacrConfig;
  private middleware: InterfaceMiddleware[] = [];
  private handlers: Map<string, InterfaceHandler> = new Map();
  private eventListeners: ((event: InterfaceEvent) => void)[] = [];
  private stats = {
    commandsProcessed: 0,
    commandsSucceeded: 0,
    commandsFailed: 0,
    averageDuration: 0,
  };
  
  // Draggr instance for unified drag & drop
  public draggr: DraggrManager;
  
  // Navigatr instance for navigation state management
  public navigatr: Navigatr;

  constructor(config: InterfacrConfig = {}) {
    this.config = config;
    
    // ✅ ENFORCE: Logging is ALWAYS available
    this.loggr = config.loggr || this._getLoggr();
    
    // ✅ ENFORCE: Undo/redo is ALWAYS available
    this.backtrackr = config.backtrackr || this._getBacktrackr();
    
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = config.actr || this._tryGetActr();
    this.profylr = config.profylr;
    this.middleware = config.middleware || [];
    this.handlers = config.handlers || new Map();

    // Initialize Draggr with integrated dependencies
    this.draggr = new DraggrManager({
      actr: this.actr,
      backtrackr: this.backtrackr,
      enableFileExport: true,
      enableMultiSelect: true,
      enableCrossView: true,
    });

    // Initialize Navigatr for navigation state management
    this.navigatr = new Navigatr('opcentral', {
      expirationTime: 24 * 60 * 60 * 1000, // 24 hours
      maxRecentItems: 10,
      useDefaults: true,
      autoSave: true,
    });

    // Register default handlers
    this.registerDefaultHandlers();

    this.log('info', 'InterfacrManager initialized with Draggr and Navigatr');
  }

  /**
   * Get Loggr instance (required dependency)
   */
  private _getLoggr(): LoggrInstance {
    try {
      const { getLoggr } = require('@opcentral/loggr');
      return getLoggr();
    } catch {
      // Fallback to console if Loggr not available
      return {
        log: (level: string, message: string, meta?: any) => {
          console.log(`[Interfacr:${level}]`, message, meta);
        }
      };
    }
  }
  
  /**
   * Get Backtrackr instance (required dependency)
   */
  private _getBacktrackr(): any {
    try {
      const { getBacktrackr } = require('@opcentral/backtrackr');
      return getBacktrackr();
    } catch {
      // Fallback to no-op if Backtrackr not available
      return {
        push: () => {},
        undo: () => {},
        redo: () => {},
        canUndo: () => false,
        canRedo: () => false
      };
    }
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }

  /**
   * Execute an interface command
   * 
   * PROFYLR INTEGRATION:
   * 1. Apply profile perspective to command (tone, style, language)
   * 2. Pass enhanced command through middleware chain
   * 3. UI packages receive profile context for rendering
   */
  async execute(command: InterfaceCommand): Promise<InterfaceResult> {
    const startTime = Date.now();
    
    try {
      this.log('debug', 'Executing interface command', { command });
      this.emitEvent({ type: 'command', command, timestamp: startTime });

      // Apply profile perspective if Profylr is available
      const enhancedCommand = await this.applyProfilePerspective(command);

      // Apply middleware chain
      const result = await this.applyMiddleware(enhancedCommand);

      // Update stats
      const duration = Date.now() - startTime;
      this.updateStats(true, duration);

      this.log('debug', 'Interface command succeeded', { command, result, duration });
      this.emitEvent({ type: 'result', command, result, timestamp: Date.now() });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.updateStats(false, duration);

      this.log('error', 'Interface command failed', { command, error });
      this.emitEvent({ 
        type: 'error', 
        command, 
        error: error as Error, 
        timestamp: Date.now() 
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        metadata: {
          duration,
          package: 'interfacr',
          operation: command.operation,
        },
      };
    }
  }

  /**
   * Register a custom handler for a command type
   */
  registerHandler(type: string, handler: InterfaceHandler): void {
    this.handlers.set(type, handler);
    this.log('debug', `Registered handler for type: ${type}`);
  }

  /**
   * Add middleware to the chain
   */
  use(middleware: InterfaceMiddleware): void {
    this.middleware.push(middleware);
    this.log('debug', 'Added middleware to chain');
  }

  /**
   * Subscribe to interface events
   */
  on(listener: (event: InterfaceEvent) => void): () => void {
    this.eventListeners.push(listener);
    return () => {
      const index = this.eventListeners.indexOf(listener);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get active profile from Profylr
   * @returns {Promise<ResolvedProfile | null>} Active profile with UI perspective
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      this.log('warn', 'Failed to get active profile', { error });
      return null;
    }
  }

  /**
   * Apply profile perspective to command parameters
   * Enhances command with profile-based UI context (tone, style, language)
   */
  private async applyProfilePerspective(command: InterfaceCommand): Promise<InterfaceCommand> {
    const profile = await this.getActiveProfile();
    
    if (!profile?.perspective && !profile?.behavior) {
      return command; // No profile or no perspective/behavior defined
    }
    
    // Merge profile perspective and behavior into command params
    const enhancedParams = {
      ...command.params,
      _profileContext: {
        perspective: profile.perspective,
        behavior: profile.behavior,
        tone: profile.perspective?.tone,
        style: profile.perspective?.style,
        language: profile.perspective?.language,
        audience: profile.perspective?.audience,
      },
    };
    
    return {
      ...command,
      params: enhancedParams,
    };
  }

  /**
   * Get statistics
   */
  getStats() {
    return { ...this.stats };
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.stats = {
      commandsProcessed: 0,
      commandsSucceeded: 0,
      commandsFailed: 0,
      averageDuration: 0,
    };
  }

  /**
   * Apply middleware chain
   */
  private async applyMiddleware(command: InterfaceCommand): Promise<InterfaceResult> {
    let index = 0;

    const next = async (): Promise<InterfaceResult> => {
      if (index < this.middleware.length) {
        const middleware = this.middleware[index++];
        return middleware(command, next);
      }
      // End of middleware chain - execute handler
      return this.executeHandler(command);
    };

    return next();
  }

  /**
   * Execute the appropriate handler for a command
   */
  private async executeHandler(command: InterfaceCommand): Promise<InterfaceResult> {
    const handlerKey = `${command.type}:${command.operation}`;
    const handler = this.handlers.get(handlerKey) || this.handlers.get(command.type);

    if (!handler) {
      throw new Error(`No handler registered for command type: ${command.type}`);
    }

    return handler(command);
  }

  /**
   * Register default handlers for built-in command types
   */
  private registerDefaultHandlers(): void {
    // Dialog handler
    this.registerHandler('dialog', async (command) => {
      // Route to Promptr/Dialoggr
      return {
        success: true,
        data: { message: 'Dialog handler not yet implemented' },
        metadata: {
          duration: 0,
          package: 'promptr',
          operation: command.operation,
        },
      };
    });

    // Panel handler
    this.registerHandler('panel', async (command) => {
      // Route to Panelr
      return {
        success: true,
        data: { message: 'Panel handler not yet implemented' },
        metadata: {
          duration: 0,
          package: 'panelr',
          operation: command.operation,
        },
      };
    });

    // Form handler
    this.registerHandler('form', async (command) => {
      // Route to Formr
      return {
        success: true,
        data: { message: 'Form handler not yet implemented' },
        metadata: {
          duration: 0,
          package: 'formr',
          operation: command.operation,
        },
      };
    });

    // Notification handler
    this.registerHandler('notification', async (command) => {
      // Route to Notifyr
      return {
        success: true,
        data: { message: 'Notification handler not yet implemented' },
        metadata: {
          duration: 0,
          package: 'notifyr',
          operation: command.operation,
        },
      };
    });

    // Navigation handler - Routes to Navigatr
    this.registerHandler('navigation', async (command) => {
      const startTime = Date.now();
      
      try {
        const { operation, params } = command;
        
        switch (operation) {
          case 'navigate':
          case 'setView':
            // Set current view
            this.navigatr.setView(params?.viewId || params?.type, params?.data);
            return {
              success: true,
              data: { viewId: params?.viewId, data: params?.data },
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'setView',
              },
            };
            
          case 'getView':
          case 'getCurrentView':
            // Get current view
            const currentView = this.navigatr.getView();
            return {
              success: true,
              data: currentView,
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'getView',
              },
            };
            
          case 'setLastUsed':
            // Track last used item
            this.navigatr.setLastUsed(params?.type, params?.data);
            return {
              success: true,
              data: { type: params?.type, data: params?.data },
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'setLastUsed',
              },
            };
            
          case 'getLastUsed':
            // Get last used item
            const lastUsed = this.navigatr.getLastUsed(params?.type);
            return {
              success: true,
              data: lastUsed,
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'getLastUsed',
              },
            };
            
          case 'getRecentViews':
            // Get recent views
            const recentViews = this.navigatr.getRecentViews(params?.type, params?.limit);
            return {
              success: true,
              data: recentViews,
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'getRecentViews',
              },
            };
            
          case 'clearState':
            // Clear navigation state
            this.navigatr.clearState();
            return {
              success: true,
              data: { cleared: true },
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'clearState',
              },
            };
            
          case 'setBreadcrumbs':
            // Set breadcrumbs
            this.navigatr.setBreadcrumbs(params?.breadcrumbs || []);
            return {
              success: true,
              data: { breadcrumbs: params?.breadcrumbs },
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'setBreadcrumbs',
              },
            };
            
          case 'getBreadcrumbs':
            // Get breadcrumbs
            const breadcrumbs = this.navigatr.getBreadcrumbs();
            return {
              success: true,
              data: breadcrumbs,
              metadata: {
                duration: Date.now() - startTime,
                package: 'navigatr',
                operation: 'getBreadcrumbs',
              },
            };
            
          default:
            throw new Error(`Unknown navigation operation: ${operation}`);
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Navigation error',
          metadata: {
            duration: Date.now() - startTime,
            package: 'navigatr',
            operation: command.operation,
          },
        };
      }
    });
  }

  /**
   * Update statistics
   */
  private updateStats(success: boolean, duration: number): void {
    this.stats.commandsProcessed++;
    if (success) {
      this.stats.commandsSucceeded++;
    } else {
      this.stats.commandsFailed++;
    }
    
    // Update average duration
    const total = this.stats.averageDuration * (this.stats.commandsProcessed - 1) + duration;
    this.stats.averageDuration = total / this.stats.commandsProcessed;
  }

  /**
   * Emit an event
   */
  private emitEvent(event: InterfaceEvent): void {
    if (!this.config.enableEvents) return;
    
    for (const listener of this.eventListeners) {
      try {
        listener(event);
      } catch (error) {
        this.log('error', 'Event listener error', { error });
      }
    }
  }

  /**
   * Log a message
   */
  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any): void {
    if (!this.config.enableLogging) return;

    if (this.loggr) {
      this.loggr.log({
        level,
        message,
        category: 'interfacr',
        data,
      });
    } else {
      console.log(`[Interfacr] [${level}] ${message}`, data || '');
    }
  }
}

/**
 * Create default Interfacr instance
 */
export function createInterfacr(config?: InterfacrConfig): InterfacrManager {
  return new InterfacrManager(config);
}
